"use client";

import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PrimaryActionButton } from "@/components/ui/PrimaryActionButton";
import { TikTokAccountCard } from "./TikTokAccountCard";
import { SiTiktok } from "react-icons/si";
import { GlassPanel } from "@/components/ui/GlassPanel";
import {
  TikTokAccount,
  useTikTokConnections,
  UseTikTokConnectionsReturn,
} from "../hooks/useTikTokConnections";

type TikTokSectionVariant = "page" | "popup";

type TikTokSectionProps = {
  hook?: UseTikTokConnectionsReturn;
  variant?: TikTokSectionVariant;
  onBack?: () => void;
  onConnectMore?: () => void;
  redirectTo?: string;
};

export function TikTokSection({
  hook: providedHook,
  variant = "page",
  onBack,
  onConnectMore,
  redirectTo,
}: TikTokSectionProps = {}) {
  const { t } = useTranslation();
  const hook = providedHook ?? useTikTokConnections();

  const {
    isLoading,
    isConnecting,
    accounts,
    indexingMessages,
    isIndexing,
    currentMessageIndex,
    triggerIndex,
    connectTikTok,
    connectTikTokBusiness,
    disconnectTikTok,
  } = hook;

  const sectionClassName = variant === "popup" ? "text-white space-y-6 w-full" : "rounded-lg shadow-sm text-white";
  const hasAccounts = accounts.length > 0;
  const connectMoreTranslation = t("account.connect_more_accounts");
  const connectMoreText =
    connectMoreTranslation && connectMoreTranslation !== "account.connect_more_accounts"
      ? connectMoreTranslation
      : "Connect more accounts";

  const handleConnect = useCallback(
    (mode: "standard" | "business") => {
      const fallbackRedirect = typeof window !== "undefined" ? window.location.pathname : "/account";
      if (mode === "business") {
        return connectTikTokBusiness(redirectTo ?? fallbackRedirect);
      }
      return connectTikTok(redirectTo ?? fallbackRedirect);
    },
    [connectTikTok, connectTikTokBusiness, redirectTo]
  );

  if (isLoading) {
    return (
      <div className={sectionClassName}>
        <div className="flex items-center justify-between mb-4">
          {variant === "popup" ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white"
                onClick={onBack}
                disabled={!onBack}
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="sr-only">Back</span>
              </Button>
              <h3 className="text-2xl font-normal">TikTok</h3>
              <div className="h-8 w-8" />
            </>
          ) : (
            <h3 className="text-2xl font-normal mb-4">TikTok</h3>
          )}
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-neutral-800 rounded w-3/4 mb-4"></div>
          <div className="h-8 bg-neutral-800 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  const header = (
    <div className="flex items-center justify-between mb-4">
      {variant === "popup" ? (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white"
            onClick={onBack}
            disabled={!onBack}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Back</span>
          </Button>
          <h3 className="text-2xl font-normal">TikTok</h3>
          <div className="h-8 w-8" />
        </>
      ) : (
        <h3 className="text-2xl font-normal mb-4">TikTok</h3>
      )}
    </div>
  );

  const content = hasAccounts ? (
    <div className="space-y-4">
      {accounts.map((account: TikTokAccount) => (
        <TikTokAccountCard
          key={account.id}
          account={account}
          onDisconnect={disconnectTikTok}
          onIndex={triggerIndex}
          isIndexing={isIndexing}
          currentMessageIndex={currentMessageIndex}
          indexingMessages={indexingMessages}
        />
      ))}
    </div>
  ) : (
    <GlassPanel className="text-center p-4 md:p-6 space-y-6 ">
      <SiTiktok className="mx-auto h-12 w-12" />
      <h3 className="text-lg font-medium">
        {t("account.no_tiktok_accounts_found") || "No TikTok Accounts Found"}
      </h3>

      <div className="space-y-3 max-w-sm mx-auto mt-6">
        <PrimaryActionButton
          size="sm"
          className="w-full"
          onClick={() => handleConnect("standard")}
          disabled={isConnecting}
        >
          <span className="inline-flex items-center justify-center h-5 w-5 rounded-md">
            <SiTiktok className="h-3.5 w-3.5" />
          </span>
          {isConnecting
            ? t("account.connecting") || "Connecting..."
            : t("account.connect_with_tiktok") || "Connect with TikTok"}
        </PrimaryActionButton>

        <PrimaryActionButton
          size="sm"
          className="w-full"
          onClick={() => handleConnect("business")}
          disabled={isConnecting}
        >
          <span className="inline-flex items-center justify-center h-5 w-5 rounded-md">
            <SiTiktok className="h-3.5 w-3.5" />
          </span>
          {isConnecting
            ? t("account.connecting") || "Connecting..."
            : t("account.connect_with_tiktok_business", "Connect your TikTok Business Account")}
        </PrimaryActionButton>
      </div>
    </GlassPanel>
  );

  const connectMoreButton =
    variant === "popup" && hasAccounts && onConnectMore ? (
      <Button
        type="button"
        variant="ghost"
        className="w-full border border-white/10 bg-white/5 text-white hover:bg-white/10"
        onClick={onConnectMore}
      >
        {connectMoreText}
      </Button>
    ) : null;

  return (
    <div className={sectionClassName}>
      {header}
      {content}
      {connectMoreButton}
    </div>
  );
}
